import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { Toast as ToastPrimitives } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * @deprecated Shadcn Toast ist deprecated.
 * Implementierung der neuen Komponente erfolgt durch Ticket: https://e2ngmbh.atlassian.net/browse/TG-21?atlOrigin=eyJpIjoiYzJjYmJkZWExNDVhNGQ1ZWE4NWQyNDdmOGU3NjQ5MmMiLCJwIjoiaiJ9
 */
function ToastProvider({
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Provider>) {
  return <ToastPrimitives.Provider data-slot="toast-provider" {...props} />;
}

/**
 * @deprecated Shadcn Toast ist deprecated.
 * Implementierung der neuen Komponente erfolgt durch Ticket: https://e2ngmbh.atlassian.net/browse/TG-21?atlOrigin=eyJpIjoiYzJjYmJkZWExNDVhNGQ1ZWE4NWQyNDdmOGU3NjQ5MmMiLCJwIjoiaiJ9
 */
function ToastViewport({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Viewport>) {
  return (
    <ToastPrimitives.Viewport
      data-toast
      data-slot="toast-viewport"
      className={cn(
        'fixed top-0 z-100 flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]',
        className,
      )}
      {...props}
    />
  );
}

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border border-neutrals-200 p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-(--radix-toast-swipe-end-x) data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x) data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full sm:data-[state=open]:slide-in-from-bottom-full dark:border-neutrals-800',
  {
    variants: {
      variant: {
        default:
          'border bg-white text-neutrals-1000 dark:bg-neutrals-1000 dark:text-neutrals-200',
        destructive:
          'destructive group border-primary-red-500 bg-primary-red-500 text-neutrals-200 dark:border-primary-red-700 dark:bg-primary-red-700 dark:text-neutrals-200',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

/**
 * @deprecated Shadcn Toast ist deprecated.
 * Implementierung der neuen Komponente erfolgt durch Ticket: https://e2ngmbh.atlassian.net/browse/TG-21?atlOrigin=eyJpIjoiYzJjYmJkZWExNDVhNGQ1ZWE4NWQyNDdmOGU3NjQ5MmMiLCJwIjoiaiJ9
 */
function Toast({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Root> &
  VariantProps<typeof toastVariants>) {
  return (
    <ToastPrimitives.Root
      data-slot="toast"
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  );
}

/**
 * @deprecated Shadcn Toast ist deprecated.
 * Implementierung der neuen Komponente erfolgt durch Ticket: https://e2ngmbh.atlassian.net/browse/TG-21?atlOrigin=eyJpIjoiYzJjYmJkZWExNDVhNGQ1ZWE4NWQyNDdmOGU3NjQ5MmMiLCJwIjoiaiJ9
 */
function ToastAction({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Action>) {
  return (
    <ToastPrimitives.Action
      data-slot="toast-action"
      className={cn(
        'inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-neutrals-300 bg-transparent px-3 text-sm font-medium ring-offset-white transition-colors hover:bg-neutrals-200 focus:outline-hidden focus:ring-2 focus:ring-neutrals-1000 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-neutrals-200/40 group-[.destructive]:hover:border-primary-red-500/30 group-[.destructive]:hover:bg-primary-red-500 group-[.destructive]:hover:text-neutrals-200 group-[.destructive]:focus:ring-primary-red-500 dark:border-neutrals-800 dark:ring-offset-neutrals-1000 dark:hover:bg-neutrals-800 dark:focus:ring-neutrals-400 dark:group-[.destructive]:border-neutrals-800/40 dark:group-[.destructive]:hover:border-primary-red-700/30 dark:group-[.destructive]:hover:bg-primary-red-700 dark:group-[.destructive]:hover:text-neutrals-200 dark:group-[.destructive]:focus:ring-primary-red-700',
        className,
      )}
      {...props}
    />
  );
}

/**
 * @deprecated Shadcn Toast ist deprecated.
 * Implementierung der neuen Komponente erfolgt durch Ticket: https://e2ngmbh.atlassian.net/browse/TG-21?atlOrigin=eyJpIjoiYzJjYmJkZWExNDVhNGQ1ZWE4NWQyNDdmOGU3NjQ5MmMiLCJwIjoiaiJ9
 */
function ToastClose({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Close>) {
  return (
    <ToastPrimitives.Close
      data-slot="toast-close"
      className={cn(
        'absolute right-2 top-2 rounded-md p-1 text-neutrals-1000/50 opacity-0 transition-opacity hover:text-neutrals-1000 focus:opacity-100 focus:outline-hidden focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-primary-red-300 group-[.destructive]:hover:text-primary-red-100 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-primary-red-500 dark:text-neutrals-200/50 dark:hover:text-neutrals-200',
        className,
      )}
      toast-close=""
      {...props}>
      <X className="h-4 w-4" />
    </ToastPrimitives.Close>
  );
}

/**
 * @deprecated Shadcn Toast ist deprecated.
 * Implementierung der neuen Komponente erfolgt durch Ticket: https://e2ngmbh.atlassian.net/browse/TG-21?atlOrigin=eyJpIjoiYzJjYmJkZWExNDVhNGQ1ZWE4NWQyNDdmOGU3NjQ5MmMiLCJwIjoiaiJ9
 */
function ToastTitle({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Title>) {
  return (
    <ToastPrimitives.Title
      data-slot="toast-title"
      className={cn('text-sm font-semibold', className)}
      {...props}
    />
  );
}

/**
 * @deprecated Shadcn Toast ist deprecated.
 * Implementierung der neuen Komponente erfolgt durch Ticket: https://e2ngmbh.atlassian.net/browse/TG-21?atlOrigin=eyJpIjoiYzJjYmJkZWExNDVhNGQ1ZWE4NWQyNDdmOGU3NjQ5MmMiLCJwIjoiaiJ9
 */
function ToastDescription({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitives.Description>) {
  return (
    <ToastPrimitives.Description
      data-slot="toast-description"
      className={cn('text-sm opacity-90', className)}
      {...props}
    />
  );
}

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  type ToastActionElement,
  type ToastProps,
};
